import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import DndUploadProgressToast from "./DndUploadProgressToast";
import { dismissDndUpload } from "state/ui/dndUploadProgress/dndUploadProgressActions";
import {
  getDndUploadProgressState,
  getVisibleDndUploads,
  getDndUploadAggregate
} from "state/ui/dndUploadProgress/dndUploadProgressSelectors";
import {
  isTimelineVisibleSelector,
  timelineHeightSelector
} from "state/ui/editorContext/editorContextSelectors";
import { TIMELINE_MIN_HEIGHT } from "lib/constants";
import { SAVING_INDICATOR_BASE_BOTTOM } from "views/components/Editor/SavingIndicator/SavingIndicator";

const PER_ENTRY_SUCCESS_AUTODISMISS_MS = 2000;

const DndUploadProgressToastContainer = () => {
  const dispatch = useDispatch();
  const uploadsState = useSelector(getDndUploadProgressState);
  const isTimelineVisible = useSelector(isTimelineVisibleSelector);
  const timelineHeight = useSelector(timelineHeightSelector);
  const hasEntries = Object.keys(uploadsState).length > 0;

  const wrappedState = useMemo(
    () => ({ ui: { dndUploadProgress: uploadsState } }),
    [uploadsState]
  );

  const visible = useMemo(() => getVisibleDndUploads(wrappedState), [
    wrappedState
  ]);
  const aggregate = useMemo(() => getDndUploadAggregate(wrappedState), [
    wrappedState
  ]);

  // Route the latest uploadsState through a ref so the interval body reads
  // the current snapshot without the effect resubscribing on every progress
  // dispatch (updateDndUploadProgress fires many times per second per file).
  const uploadsStateRef = useRef(uploadsState);
  uploadsStateRef.current = uploadsState;

  useEffect(() => {
    if (!hasEntries) return undefined;
    const interval = setInterval(() => {
      const now = Date.now();
      for (const entry of Object.values(uploadsStateRef.current)) {
        if (
          entry.status === "success" &&
          entry.completedAt !== undefined &&
          now - entry.completedAt >= PER_ENTRY_SUCCESS_AUTODISMISS_MS
        ) {
          dispatch(dismissDndUpload({ uploadId: entry.uploadId }));
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, [hasEntries, dispatch]);

  const onDismissAll = useCallback(() => {
    Object.keys(uploadsState).forEach(uploadId => {
      const entry = uploadsState[uploadId];
      if (entry && entry.status !== "uploading") {
        dispatch(dismissDndUpload({ uploadId }));
      }
    });
  }, [uploadsState, dispatch]);

  const positionStyle = useMemo(() => {
    const extraOffset =
      isTimelineVisible && timelineHeight
        ? Math.max(0, timelineHeight - TIMELINE_MIN_HEIGHT)
        : 0;
    return { bottom: `${SAVING_INDICATOR_BASE_BOTTOM + extraOffset}px` };
  }, [isTimelineVisible, timelineHeight]);

  return (
    <DndUploadProgressToast
      uploads={visible}
      aggregate={aggregate}
      onDismissAll={onDismissAll}
      positionStyle={positionStyle}
    />
  );
};

export default DndUploadProgressToastContainer;
